import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();
  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;
  /** @type {boolean} */
  #pausedByVisibility = false;
  /** @type {WeakSet<HTMLVideoElement>} */
  #observedVideos = new WeakSet();
  /** @type {WeakSet<HTMLVideoElement>} */
  #mobileAutoplayRecoveryApplied = new WeakSet();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStartedPlaying, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    if (this.#isGalleryManagedMedia()) {
      this.#setupVisibilityObserver();
    }

    // For PDP/gallery autoplay media, start only when eligible and visible.
    if (this.hasAttribute('autoplay') && this.#isGalleryManagedMedia()) {
      requestAnimationFrame(() => {
        this.#startAutoplayIfEligible();
      });
    }

    queueMicrotask(() => this.#syncAutoplayPresentation());
    queueMicrotask(() => this.#setupVideoStateSync());
  }

  /**
   * Autoplay markup injects video/iframe as a direct child, but the poster button stays visible until
   * `loadContent` runs (click). Match that state so CSS can hide the poster and duplicate preview image.
   */
  #syncAutoplayPresentation() {
    if (!this.hasAttribute('autoplay')) return;

    if (this.hasAttribute('data-card-hover-video') && prefersReducedMotion()) return;

    const hasInlineMedia = this.querySelector(':scope > video, :scope > iframe');
    if (!hasInlineMedia) return;

    const posterButton = this.querySelector('.deferred-media__poster-button');
    if (posterButton instanceof HTMLElement) {
      posterButton.classList.add('deferred-media__playing');
    }

    this.isPlaying = true;
    this.updatePlayPauseHint(true);
  }

  #prepareVideoForAutoplay(video) {
    if (!(video instanceof HTMLVideoElement)) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  }

  #playVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;

    const play = () => {
      video.play().catch(() => {});
    };

    const signal = this.#abortController.signal;
    if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
      video.addEventListener('loadeddata', play, { once: true, signal });
      video.addEventListener('canplay', play, { once: true, signal });
    }

    play();
  }

  /**
   * iOS/Safari can report autoplay progress while the first frame is still not painted.
   * If we detect that state on mobile, do a one-time restart from 0 to force frame rendering.
   * @param {HTMLVideoElement} video
   */
  #scheduleMobileAutoplayRecovery(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    if (!this.hasAttribute('autoplay')) return;
    if (!this.#shouldUseNativeMobileControls()) return;
    if (this.#mobileAutoplayRecoveryApplied.has(video)) return;

    let hasRenderedFrame = false;
    if (typeof video.requestVideoFrameCallback === 'function') {
      video.requestVideoFrameCallback(() => {
        hasRenderedFrame = true;
      });
    } else {
      const signal = this.#abortController.signal;
      video.addEventListener(
        'playing',
        () => {
          hasRenderedFrame = true;
        },
        { once: true, signal }
      );
    }

    window.setTimeout(() => {
      if (this.#abortController.signal.aborted) return;
      if (!this.isConnected || !video.isConnected) return;
      if (hasRenderedFrame) return;
      if (video.paused || video.currentTime <= 0) return;

      this.#mobileAutoplayRecoveryApplied.add(video);

      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        return;
      }

      requestAnimationFrame(() => {
        if (this.#abortController.signal.aborted) return;
        if (!this.isConnected || !video.isConnected) return;
        this.#playVideo(video);
      });
    }, 320);
  }

  #shouldUseNativeMobileControls() {
    return window.matchMedia('(max-width: 749px)').matches && this.closest('.product-media') != null;
  }

  #applyNativeMobileVideoControls() {
    if (!this.#shouldUseNativeMobileControls()) return;

    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    video.controls = true;
    video.setAttribute('controls', '');
  }

  #setupVideoStateSync() {
    const video = this.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;
    if (this.#observedVideos.has(video)) return;

    const signal = this.#abortController.signal;
    video.addEventListener('play', this.#handleNativeVideoPlay, { signal });
    video.addEventListener('pause', this.#handleNativeVideoPause, { signal });
    video.addEventListener('ended', this.#handleNativeVideoPause, { signal });
    this.#observedVideos.add(video);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    const hideToggleButton = this.hasAttribute('data-hide-toggle-media-button');
    if (toggleMediaButton instanceof HTMLElement) {
      if (this.#shouldUseNativeMobileControls()) {
        toggleMediaButton.classList.add('hidden');
        return;
      }

      if (hideToggleButton) {
        toggleMediaButton.classList.add('hidden');
        return;
      }

      toggleMediaButton.classList.remove('hidden');
      toggleMediaButton.classList.toggle('is-paused', !isPlaying);
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    if (this.getAttribute('data-media-loaded')) {
      this.playMedia();
      return;
    }

    this.loadContent(true);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);
    this.#setupVideoStateSync();
    this.#applyNativeMobileVideoControls();

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // Safari requires muted + playsinline to be enforced on the runtime element.
      this.#prepareVideoForAutoplay(content);
      this.#playVideo(content);
      this.#scheduleMobileAutoplayRecovery(content);
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    this.#pausedByVisibility = false;
    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video instanceof HTMLVideoElement && this.hasAttribute('autoplay')) {
        this.#prepareVideoForAutoplay(video);
      }
      if (video instanceof HTMLVideoElement) {
        this.#playVideo(video);
        this.#scheduleMobileAutoplayRecovery(video);
      }
    }
    this.#applyNativeMobileVideoControls();
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // Inline autoplay videos render media nodes immediately but may not set
    // data-media-loaded; only update hints when media is actually present.
    if (!this.getAttribute('data-media-loaded') && !this.#hasLoadedMedia()) return;

    this.updatePlayPauseHint(this.isPlaying);
  }

  #handleMediaStartedPlaying = (event) => {
    const source = event.detail.resource;
    if (source === this) return;
    if (!(source instanceof HTMLElement)) return;
    if (!this.#sharesPlaybackScopeWith(source)) return;

    const thisSlide = this.closest('slideshow-slide');
    const sourceSlide = source.closest('slideshow-slide');
    if (thisSlide instanceof HTMLElement && sourceSlide instanceof HTMLElement) {
      const thisIsActive = thisSlide.getAttribute('aria-hidden') === 'false';
      const sourceIsActive = sourceSlide.getAttribute('aria-hidden') === 'false';

      // Ignore hidden slide media trying to steal playback from the active slide.
      if (thisIsActive && !sourceIsActive) {
        return;
      }
    }
    if (thisSlide instanceof HTMLElement && sourceSlide == null) {
      const thisIsActive = thisSlide.getAttribute('aria-hidden') === 'false';
      // Ignore non-slideshow media trying to steal playback from the active slide.
      if (thisIsActive) {
        return;
      }
    }

    this.pauseMedia();
  };

  #handleNativeVideoPlay = () => {
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
    this.isPlaying = true;
    this.updatePlayPauseHint(true);
  };

  #handleNativeVideoPause = () => {
    this.isPlaying = false;
    this.updatePlayPauseHint(false);
  };

  #setupVisibilityObserver() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (!entry.isIntersecting) {
          if (this.#isActiveSlideshowSlideMedia()) return;

          if (this.isPlaying) {
            this.#pausedByVisibility = true;
          }
          this.pauseMedia();
          return;
        }

        if (!this.hasAttribute('autoplay')) return;
        if (this.isPlaying) return;
        this.#startAutoplayIfEligible();
      },
      {
        threshold: 0.15,
      }
    );
    this.#visibilityObserver.observe(this);
  }

  #hasLoadedMedia() {
    return this.querySelector('video, iframe[data-video-type]') != null;
  }

  #isPrimaryAutoplayMedia() {
    const scope = this.closest('media-gallery') || this.closest('.shopify-section, dialog') || document;

    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    const activeSlideshowAutoplayMedia = isDesktop
      ? scope.querySelector('slideshow-slide[aria-hidden="false"] deferred-media[autoplay]')
      : null;
    if (activeSlideshowAutoplayMedia instanceof HTMLElement) {
      return activeSlideshowAutoplayMedia === this;
    }

    const autoplayMedia = Array.from(scope.querySelectorAll('deferred-media[autoplay]'));
    if (!autoplayMedia.length) return true;

    const firstVisibleAutoplayMedia =
      autoplayMedia.find((media) => this.#isElementVisibleInViewport(media)) || autoplayMedia[0];

    return firstVisibleAutoplayMedia === this;
  }

  #isVisibleInViewport() {
    return this.#isElementVisibleInViewport(this);
  }

  #isElementVisibleInViewport(element) {
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
    const visibleArea = visibleWidth * visibleHeight;
    const totalArea = rect.width * rect.height;

    if (!totalArea) return false;

    return visibleArea / totalArea >= 0.15;
  }

  #isGalleryManagedMedia() {
    return this.closest('media-gallery') != null;
  }

  #isActiveSlideshowSlideMedia() {
    const slide = this.closest('slideshow-slide');
    if (!(slide instanceof HTMLElement)) return false;

    return slide.getAttribute('aria-hidden') === 'false';
  }

  #sharesPlaybackScopeWith(element) {
    return this.#getPlaybackScope(this) === this.#getPlaybackScope(element);
  }

  #getPlaybackScope(element) {
    return (
      element.closest('.media-gallery__grid') ||
      element.closest('slideshow-component') ||
      element.closest('media-gallery') ||
      element.closest('dialog') ||
      element.closest('.shopify-section') ||
      document
    );
  }

  #startAutoplayIfEligible() {
    if (!this.isConnected) return;

    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    if (isDesktop && this.closest('slideshow-slide') && !this.#isActiveSlideshowSlideMedia()) {
      this.pauseMedia();
      return;
    }

    if (!this.#isPrimaryAutoplayMedia()) {
      this.pauseMedia();
      return;
    }

    if (!this.#isVisibleInViewport()) {
      this.pauseMedia();
      return;
    }

    if (!this.#hasLoadedMedia()) {
      this.loadContent(false);
    }

    this.playMedia();
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
